import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Box, Grid, HStack, Text, VStack} from 'platform/foundation';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  rateMatrixCellFormSchema,
  RateMatrixCellFormValues,
} from '../RateMatrixCellForm/rateMatrixCellFormSchema';
import {RateMatrixCellData} from '../types/RateMatrixCellData';

interface RateMatrixBulkRatesFormProps extends RequiredTestIdProps {
  selectedCount: number;
  onSubmit: (data: RateMatrixCellData) => void;
  onRemove: VoidFunction;
}

const DEFAULT_VALUES: RateMatrixCellFormValues = {nominalRate: '', apr: ''};

export function RateMatrixBulkRatesForm(props: RateMatrixBulkRatesFormProps) {
  // `FormSubmitHandler` requires a Promise return, and `require-await` bans a bare `async` here.
  const handleSubmit: FormSubmitHandler<RateMatrixCellFormValues> = (values) => {
    props.onSubmit(values);
    closeCurrentDialog();

    return Promise.resolve();
  };

  const handleRemove = () => {
    props.onRemove();
    closeCurrentDialog();
  };

  return (
    <Form<RateMatrixCellFormValues>
      experimentalZodSchema={rateMatrixCellFormSchema}
      defaultValues={DEFAULT_VALUES}
      onSubmit={handleSubmit}
      data-testid={suffixTestId('bulkRatesForm', props)}
    >
      {(control) => (
        <VStack spacing={4}>
          <Text color="secondary">
            {i18n.t('page.vehicle.financingAndLeasing.bulkEditRatesDescription', {
              count: props.selectedCount,
            })}
          </Text>
          <Grid columns={2}>
            <FormField
              control={control}
              name="nominalRate"
              type="text"
              isRequired
              suffix="%"
              label={i18n.t('entity.vehicleFinancingTemplate.labels.nominalRate')}
              data-testid={suffixTestId('nominalRate', props)}
            />
            <FormField
              control={control}
              name="apr"
              type="text"
              isRequired
              suffix="%"
              label={i18n.t('entity.vehicleFinancingTemplate.labels.apr')}
              data-testid={suffixTestId('apr', props)}
            />
          </Grid>
          <HStack align="center">
            <Button
              variant="dangerLink"
              title={i18n.t('general.actions.remove')}
              onClick={handleRemove}
              data-testid={suffixTestId('remove', props)}
            />
            <Box flexGrow={1}>
              <ButtonGroup align="right">
                <Button
                  variant="secondary"
                  title={i18n.t('general.actions.cancel')}
                  onClick={closeCurrentDialog}
                  data-testid={suffixTestId('cancel', props)}
                />
                <FormButton
                  control={control}
                  type="submit"
                  variant="primary"
                  title={i18n.t('general.actions.apply')}
                  data-testid={suffixTestId('apply', props)}
                />
              </ButtonGroup>
            </Box>
          </HStack>
        </VStack>
      )}
    </Form>
  );
}
